import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";

export default function PublicLayout({ children }) {
  const location = useLocation();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header
        data-testid="public-header"
        className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[var(--rciq-line)]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center group" data-testid="brand-link">
            <img
              src="https://www.therevenuecycleiq.com/images/logo.png"
              alt="Revenue Cycle IQ"
              className="h-10 md:h-14 w-auto object-contain group-hover:opacity-90 transition-opacity"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-6 text-sm font-600">
            <a href="https://www.therevenuecycleiq.com" target="_blank" rel="noopener noreferrer" className="link-underline" data-testid="nav-home">Home</a>
            <a href="https://www.therevenuecycleiq.com/about" target="_blank" rel="noopener noreferrer" className="link-underline" data-testid="nav-about">About</a>
            <a href="https://www.therevenuecycleiq.com/services" target="_blank" rel="noopener noreferrer" className="link-underline" data-testid="nav-services">Services</a>
            <a href="https://intelligence.therevenuecycleiq.com" target="_blank" rel="noopener noreferrer" className="link-underline" data-testid="nav-denial-intelligence">Denial Intelligence</a>
            <a href="https://forecast.therevenuecycleiq.com" target="_blank" rel="noopener noreferrer" className="link-underline" data-testid="nav-revenue-forecast">Revenue Forecast</a>
            <Link
              to="/"
              className={`link-underline ${location.pathname === "/" ? "text-[var(--rciq-teal-dark)]" : ""}`}
              data-testid="nav-insights"
            >
              Insights
            </Link>
            <a href="https://www.therevenuecycleiq.com/contact" target="_blank" rel="noopener noreferrer" className="link-underline" data-testid="nav-contact">Contact</a>
          </nav>

          <div className="flex items-center gap-3">
            {user && user.role === "admin" && (
              <Link to="/admin" className="hidden sm:inline text-xs font-700 text-[var(--rciq-teal-dark)] hover:underline" data-testid="nav-admin">
                Admin
              </Link>
            )}
            <a
              href="https://www.therevenuecycleiq.com/book-a-call" target="_blank" rel="noopener noreferrer"
              className="hidden sm:inline-flex btn-pill btn-pill-primary text-[12px]"
              data-testid="cta-book-call"
            >
              Book a Call
            </a>

            {/* Mobile hamburger toggle */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen((o) => !o)}
              className="lg:hidden w-11 h-11 flex items-center justify-center rounded-lg text-[var(--rciq-ink)] hover:bg-[var(--rciq-bg-tint)] transition"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-[var(--rciq-line)] bg-white" data-testid="mobile-menu-panel">
            <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1 text-base font-600">
              <a href="https://www.therevenuecycleiq.com" target="_blank" rel="noopener noreferrer" onClick={closeMobileMenu} className="py-3 border-b border-[var(--rciq-line)]" data-testid="mobile-nav-home">Home</a>
              <a href="https://www.therevenuecycleiq.com/about" target="_blank" rel="noopener noreferrer" onClick={closeMobileMenu} className="py-3 border-b border-[var(--rciq-line)]" data-testid="mobile-nav-about">About</a>
              <a href="https://www.therevenuecycleiq.com/services" target="_blank" rel="noopener noreferrer" onClick={closeMobileMenu} className="py-3 border-b border-[var(--rciq-line)]" data-testid="mobile-nav-services">Services</a>
              <a href="https://intelligence.therevenuecycleiq.com" target="_blank" rel="noopener noreferrer" onClick={closeMobileMenu} className="py-3 border-b border-[var(--rciq-line)]" data-testid="mobile-nav-denial-intelligence">Denial Intelligence</a>
              <a href="https://forecast.therevenuecycleiq.com" target="_blank" rel="noopener noreferrer" onClick={closeMobileMenu} className="py-3 border-b border-[var(--rciq-line)]" data-testid="mobile-nav-revenue-forecast">Revenue Forecast</a>
              <Link to="/" onClick={closeMobileMenu} className={`py-3 border-b border-[var(--rciq-line)] ${location.pathname === "/" ? "text-[var(--rciq-teal-dark)]" : ""}`} data-testid="mobile-nav-insights">Insights</Link>
              <a href="https://www.therevenuecycleiq.com/contact" target="_blank" rel="noopener noreferrer" onClick={closeMobileMenu} className="py-3 border-b border-[var(--rciq-line)]" data-testid="mobile-nav-contact">Contact</a>
              <a
                href="https://www.therevenuecycleiq.com/book-a-call" target="_blank" rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="mt-4 btn-pill btn-pill-primary text-[12px] justify-center"
                data-testid="mobile-cta-book-call"
              >
                Book a Call
              </a>
              {user && user.role === "admin" && (
                <Link to="/admin" onClick={closeMobileMenu} className="mt-2 text-center text-[var(--rciq-teal-dark)] font-700 py-2" data-testid="mobile-nav-admin">
                  Admin Dashboard →
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-[var(--rciq-navy)] text-white mt-24" data-testid="public-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-3 gap-12">
          <div>
            <div className="inline-block rounded-2xl ring-1 ring-slate-200 shadow-xl overflow-hidden transition-transform duration-500 hover:scale-[1.02] hover:-rotate-1 bg-white">
              <img
                src="https://www.therevenuecycleiq.com/images/logo.png"
                alt="Revenue Cycle IQ — Insights That Drive Revenue"
                className="h-32 sm:h-40 w-auto object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>
            <p className="mt-6 text-sm text-white/70 max-w-sm leading-relaxed">
              Helping healthcare organizations improve financial performance through
              smarter revenue cycle strategy, analytics, and operational improvement.
            </p>
          </div>
          <div>
            <div className="eyebrow eyebrow-light mb-4">Explore</div>
            <ul className="space-y-2.5 text-sm text-white/80">
              <li><a href="https://www.therevenuecycleiq.com" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--rciq-teal)] transition">Home</a></li>
              <li><a href="https://www.therevenuecycleiq.com/services" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--rciq-teal)] transition">Services</a></li>
              <li><Link to="/" className="hover:text-[var(--rciq-teal)] transition">Insights</Link></li>
              <li><a href="https://www.therevenuecycleiq.com/contact" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--rciq-teal)] transition">Contact</a></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow eyebrow-light mb-4">Get In Touch</div>
            <a
              href="https://www.therevenuecycleiq.com/book-a-call" target="_blank" rel="noopener noreferrer"
              className="btn-pill btn-pill-primary text-[12px]"
              data-testid="footer-cta-book-call"
            >
              Book a Consultation
            </a>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 text-xs text-white/50 flex flex-col md:flex-row justify-between gap-2">
            <span>© {new Date().getFullYear()} Revenue Cycle IQ. All rights reserved.</span>
            <span>Healthcare Revenue Cycle Consulting</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
